/**
 * One command registry for workspace modes, panels, and chrome.
 * Menus, tooltips, action search, and shortcut help read this — they do not
 * invent a second binding list.
 *
 * Hide/show cloaks chrome. It does not rewrite panel flags, dock layout, or selection,
 * so showing the UI again restores the exact visibility and dimensions.
 */

export type WorkspaceMode = 'chat' | 'board' | 'flow'

export type PanelId = 'sidebar' | 'files' | 'library' | 'activity' | 'tools' | 'genie' | 'apps'

export type WorkspacePlatform = 'mac' | 'other'

export interface WorkspaceHost {
  modes: readonly WorkspaceMode[]
  panels: readonly PanelId[]
  /** Board zoom keys. Flow zoom stays on the canvas handler so it is not fired twice. */
  boardZoom: boolean
  /** Download the current board selection. Not a paid run. */
  exportSelection: boolean
}

export const DESKTOP_HOST: WorkspaceHost = {
  modes: ['chat', 'board', 'flow'],
  panels: ['sidebar', 'files', 'library', 'activity', 'tools', 'genie', 'apps'],
  boardZoom: true,
  exportSelection: true,
}

export const WEB_HOST: WorkspaceHost = {
  modes: ['board', 'flow'],
  panels: ['sidebar', 'files', 'library', 'activity', 'tools'],
  boardZoom: true,
  exportSelection: false,
}

export interface ChromeVisibility {
  sidebar: boolean
  files: boolean
  library: boolean
  activity: boolean
  tools: boolean
  genie: boolean
  apps: boolean
}

export interface ChromeState {
  mode: WorkspaceMode
  /** Panel open flags. Hide/show must keep this reference. */
  visibility: ChromeVisibility
  /** Dock split/width token. Hide/show must keep this reference. */
  layout: unknown
  /** Selected frame or node ids. Hide/show must keep this reference. */
  selection: readonly string[]
  /** True while toolbars and panels are cloaked, not deleted. */
  chromeHidden: boolean
  editorOpen: boolean
}

export interface WorkspaceKeyEvent {
  key: string
  code?: string
  metaKey?: boolean
  ctrlKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
  repeat?: boolean
  isComposing?: boolean
  keyCode?: number
}

export interface KeyTarget {
  tagName?: string
  isContentEditable?: boolean
  value?: string
  closest?: (selector: string) => unknown
}

export interface WorkspaceKeyState {
  host: WorkspaceHost
  mode: WorkspaceMode
  chromeHidden: boolean
  editorOpen: boolean
  selectionCount: number
  target?: KeyTarget | null
}

export type CommandKind = 'mode' | 'panel' | 'chrome' | 'help' | 'search' | 'view' | 'export' | 'delegated' | 'unavailable'

export interface WorkspaceCommand {
  id: string
  label: string
  group: string
  keywords: string[]
  kind: CommandKind
  /** Panel this command toggles, when kind is panel. */
  panel?: PanelId
  mode?: WorkspaceMode
  shortcutMac: string
  shortcutOther: string
  /** When false, help lists the command and the matcher does not run it. */
  available: boolean
  unavailableReason?: string
}

export type WorkspaceEffect =
  | { type: 'none' }
  | { type: 'mode'; mode: WorkspaceMode }
  | { type: 'panel'; panel: PanelId; open: boolean }
  | { type: 'chrome'; hidden: boolean }
  | { type: 'help' }
  | { type: 'search'; which: 'actions' | 'tools' }
  | { type: 'zoom'; action: 'in' | 'out' | 'fit' | 'selection' | 'reset' }
  | { type: 'export' }

interface Binding {
  id: string
  key: string
  code?: string
  mod: boolean
  shift: boolean
  alt: boolean
}

const BINDINGS: Binding[] = [
  { id: 'mode.chat', key: '1', code: 'Digit1', mod: true, shift: false, alt: false },
  { id: 'mode.board', key: '2', code: 'Digit2', mod: true, shift: false, alt: false },
  { id: 'mode.flow', key: '3', code: 'Digit3', mod: true, shift: false, alt: false },
  { id: 'chrome.toggle-ui', key: '\\', code: 'Backslash', mod: true, shift: false, alt: false },
  { id: 'panel.sidebar', key: 's', code: 'KeyS', mod: true, shift: true, alt: false },
  { id: 'panel.files', key: 'f', code: 'KeyF', mod: true, shift: true, alt: false },
  { id: 'panel.library', key: 'l', code: 'KeyL', mod: true, shift: true, alt: false },
  { id: 'panel.activity', key: 'a', code: 'KeyA', mod: true, shift: true, alt: false },
  { id: 'panel.tools', key: 't', code: 'KeyT', mod: true, shift: true, alt: false },
  { id: 'panel.genie', key: 'g', code: 'KeyG', mod: true, shift: true, alt: false },
  { id: 'panel.apps', key: 'o', code: 'KeyO', mod: true, shift: true, alt: false },
  { id: 'search.actions', key: 'k', code: 'KeyK', mod: true, shift: false, alt: false },
  { id: 'search.tools', key: 'k', code: 'KeyK', mod: true, shift: true, alt: false },
  { id: 'help.shortcuts', key: '/', code: 'Slash', mod: true, shift: false, alt: false },
  { id: 'view.zoom-in', key: '=', code: 'Equal', mod: true, shift: false, alt: false },
  { id: 'view.zoom-out', key: '-', code: 'Minus', mod: true, shift: false, alt: false },
  { id: 'view.fit-canvas', key: '0', code: 'Digit0', mod: true, shift: false, alt: false },
  { id: 'view.fit-selection', key: '2', code: 'Digit2', mod: false, shift: true, alt: false },
  { id: 'view.zoom-100', key: '0', code: 'Digit0', mod: true, shift: true, alt: false },
  { id: 'edit.export', key: 'e', code: 'KeyE', mod: true, shift: true, alt: false },
]

/** OS-reserved chords this registry must never claim. */
const RESERVED = new Set(['q', 'w', 'h', 'm', ' ', 'tab'])

const BLOCK_SELECTORS = [
  '[role="dialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="combobox"]',
  '[data-modal="true"]',
  '[data-genie-player]',
]

const MODE_LABEL: Record<WorkspaceMode, string> = { chat: 'Chat', board: 'Board', flow: 'Flow' }

function macChord(binding: Binding): string {
  const key = binding.key === '\\' ? '\\' : binding.key === '/' ? '/' : binding.key.toUpperCase()
  return `${binding.shift ? '⇧' : ''}${binding.mod ? '⌘' : ''}${key}`
}

function otherChord(binding: Binding): string {
  const key = binding.key === '\\' ? '\\' : binding.key === '/' ? '/' : binding.key.toUpperCase()
  return `${binding.mod ? 'Ctrl+' : ''}${binding.shift ? 'Shift+' : ''}${key}`
}

function bindingFor(id: string): Binding | undefined {
  return BINDINGS.find(binding => binding.id === id)
}

function labels(id: string): { shortcutMac: string; shortcutOther: string } {
  const binding = bindingFor(id)
  if (!binding) return { shortcutMac: '—', shortcutOther: '—' }
  return { shortcutMac: macChord(binding), shortcutOther: otherChord(binding) }
}

function command(
  partial: Omit<WorkspaceCommand, 'shortcutMac' | 'shortcutOther' | 'available' | 'keywords'> & {
    keywords?: string[]
    available?: boolean
    shortcutMac?: string
    shortcutOther?: string
  },
): WorkspaceCommand {
  return {
    keywords: partial.keywords ?? [],
    available: partial.available ?? true,
    ...labels(partial.id),
    ...partial,
  }
}

export function detectPlatform(): WorkspacePlatform {
  if (typeof navigator === 'undefined') return 'other'
  return /Mac|iPhone|iPad/i.test(navigator.platform) ? 'mac' : 'other'
}

export function shortcutLabel(id: string, platform: WorkspacePlatform): string {
  const labelsFor = labels(id)
  return platform === 'mac' ? labelsFor.shortcutMac : labelsFor.shortcutOther
}

/** Held Cmd/Ctrl+Enter must not submit another paid run. */
export function blocksRepeatedRun(event: { repeat?: boolean; key: string; metaKey?: boolean; ctrlKey?: boolean }): boolean {
  return Boolean(event.repeat) && (Boolean(event.metaKey) || Boolean(event.ctrlKey)) && event.key === 'Enter'
}

export function bindingConflicts(): { shortcut: string; ids: string[] }[] {
  const groups = new Map<string, string[]>()
  for (const binding of BINDINGS) {
    const shortcut = `${binding.mod ? 'mod+' : ''}${binding.shift ? 'shift+' : ''}${binding.alt ? 'alt+' : ''}${binding.code ?? binding.key}`
    groups.set(shortcut, [...(groups.get(shortcut) ?? []), binding.id])
  }
  return [...groups.entries()].filter(([, ids]) => ids.length > 1).map(([shortcut, ids]) => ({ shortcut, ids }))
}

export function claimsReservedKey(): string[] {
  return BINDINGS.filter(binding => binding.mod && !binding.shift && !binding.alt && RESERVED.has(binding.key.toLowerCase())).map(binding => binding.id)
}

function hostHasMode(host: WorkspaceHost, mode: WorkspaceMode): boolean {
  return host.modes.includes(mode)
}

function hostHasPanel(host: WorkspaceHost, panel: PanelId): boolean {
  return host.panels.includes(panel)
}

function describe(host: WorkspaceHost, mode: WorkspaceMode, selectionCount: number, chromeHidden: boolean): WorkspaceCommand[] {
  const modeCommand = (id: string, target: WorkspaceMode): WorkspaceCommand => command({
    id,
    label: `${MODE_LABEL[target]} mode`,
    group: 'Modes',
    kind: 'mode',
    mode: target,
    keywords: [target, 'mode', 'switch'],
    available: hostHasMode(host, target),
    unavailableReason: hostHasMode(host, target) ? undefined : 'This workspace does not offer that mode',
  })
  const panelCommand = (id: string, panel: PanelId, label: string, keywords: string[]): WorkspaceCommand => {
    const offered = hostHasPanel(host, panel)
    const modeOk = panel !== 'apps' || mode === 'chat'
    return command({
      id,
      label,
      group: 'Panels',
      kind: 'panel',
      panel,
      keywords,
      available: offered && modeOk && !chromeHidden,
      unavailableReason: !offered
        ? 'This workspace does not have that panel'
        : !modeOk
          ? 'Apps is only available in Chat'
          : chromeHidden
            ? 'Show the interface before toggling one panel'
            : undefined,
    })
  }
  return [
    modeCommand('mode.chat', 'chat'),
    modeCommand('mode.board', 'board'),
    modeCommand('mode.flow', 'flow'),
    command({
      id: 'chrome.toggle-ui',
      label: chromeHidden ? 'Show all toolbars and panels' : 'Hide all toolbars and panels',
      group: 'View',
      kind: 'chrome',
      keywords: ['hide', 'show', 'ui', 'chrome', 'panels', 'toolbars'],
    }),
    panelCommand('panel.sidebar', 'sidebar', 'Tool sidebar', ['sidebar', 'tools', 'rail']),
    panelCommand('panel.files', 'files', 'File Manager', ['files', 'file manager']),
    panelCommand('panel.library', 'library', 'Library', ['library', 'models']),
    panelCommand('panel.activity', 'activity', 'Activity', ['activity', 'jobs']),
    panelCommand('panel.tools', 'tools', 'Tools', ['tools', 'inspector', 'node controls']),
    panelCommand('panel.genie', 'genie', 'Genie', ['genie', 'chat']),
    panelCommand('panel.apps', 'apps', 'Apps', ['apps']),
    command({
      id: 'search.actions',
      label: 'Action search',
      group: 'Search',
      kind: 'search',
      keywords: ['search', 'command', 'palette', 'actions'],
      available: mode !== 'flow',
      unavailableReason: mode === 'flow' ? 'Flow uses the canvas command palette for ⌘K' : undefined,
    }),
    command({
      id: 'search.tools',
      label: 'Search tools and models',
      group: 'Search',
      kind: 'search',
      keywords: ['search', 'tools', 'models', 'library'],
      available: hostHasPanel(host, 'library') && !chromeHidden,
      unavailableReason: chromeHidden ? 'Show the interface before searching panels' : 'Library is not available here',
    }),
    command({
      id: 'help.shortcuts',
      label: 'Keyboard shortcuts',
      group: 'Help',
      kind: 'help',
      keywords: ['help', 'shortcuts', 'keyboard'],
    }),
    command({
      id: 'view.zoom-in',
      label: 'Zoom in',
      group: 'View',
      kind: 'view',
      keywords: ['zoom'],
      available: host.boardZoom && mode === 'board' && !chromeHidden,
      unavailableReason: mode === 'flow' ? 'Flow zoom stays on the canvas shortcut' : mode !== 'board' ? 'Zoom needs the board' : undefined,
    }),
    command({
      id: 'view.zoom-out',
      label: 'Zoom out',
      group: 'View',
      kind: 'view',
      keywords: ['zoom'],
      available: host.boardZoom && mode === 'board' && !chromeHidden,
      unavailableReason: mode !== 'board' ? 'Zoom needs the board' : undefined,
    }),
    command({
      id: 'view.fit-canvas',
      label: 'Fit canvas',
      group: 'View',
      kind: 'view',
      keywords: ['fit', 'zoom'],
      available: host.boardZoom && mode === 'board' && !chromeHidden,
      unavailableReason: mode === 'flow' ? 'Flow fit stays on the canvas shortcut' : mode !== 'board' ? 'Fit needs the board' : undefined,
    }),
    command({
      id: 'view.zoom-100',
      label: 'Zoom to 100%',
      group: 'View',
      kind: 'view',
      keywords: ['zoom', '100'],
      available: host.boardZoom && mode === 'board' && !chromeHidden,
      unavailableReason: mode !== 'board' ? 'Zoom needs the board' : undefined,
    }),
    command({
      id: 'view.fit-selection',
      label: 'Fit selection',
      group: 'View',
      kind: 'view',
      keywords: ['fit', 'selection'],
      available: host.boardZoom && mode === 'board' && selectionCount > 0 && !chromeHidden,
      unavailableReason: mode !== 'board' ? 'Fit selection uses the Board viewport' : 'Select a frame first',
    }),
    command({
      id: 'edit.export',
      label: 'Export selection',
      group: 'Edit',
      kind: 'export',
      keywords: ['export', 'download'],
      available: host.exportSelection && mode === 'board' && selectionCount > 0 && !chromeHidden,
      unavailableReason: !host.exportSelection
        ? 'Export is not wired on this surface'
        : mode !== 'board'
          ? 'Export the board selection'
          : selectionCount === 0
            ? 'Select a frame first'
            : undefined,
    }),
    command({
      id: 'edit.group',
      label: 'Group selection',
      group: 'Edit',
      kind: 'unavailable',
      keywords: ['group'],
      available: false,
      unavailableReason: 'Grouping stays on the frame menu until it can run without moving artwork',
    }),
    command({
      id: 'edit.arrange',
      label: 'Arrange selection',
      group: 'Edit',
      kind: 'unavailable',
      keywords: ['arrange'],
      available: false,
      unavailableReason: 'Arrangement stays on the frame menu so a shortcut cannot move artwork by accident',
    }),
    command({
      id: 'run.generate',
      label: 'Run selected',
      group: 'Run',
      kind: 'delegated',
      keywords: ['run', 'generate'],
      available: mode === 'flow',
      unavailableReason: mode === 'flow' ? undefined : 'Run stays on the canvas control, not a workspace chord',
      shortcutMac: '⌘↵',
      shortcutOther: 'Ctrl+Enter',
    }),
    command({
      id: 'run.cancel',
      label: 'Cancel run',
      group: 'Run',
      kind: 'unavailable',
      keywords: ['cancel', 'stop'],
      available: false,
      unavailableReason: 'Cancel stays on the job control so a held key cannot start or stop a paid run',
    }),
    command({
      id: 'edit.select-all',
      label: 'Select all',
      group: 'Edit',
      kind: 'delegated',
      keywords: ['select'],
      available: mode !== 'chat',
      unavailableReason: mode === 'chat' ? 'Chat has no canvas selection' : undefined,
      shortcutMac: '⌘A',
      shortcutOther: 'Ctrl+A',
    }),
    command({
      id: 'edit.delete',
      label: 'Delete selection',
      group: 'Edit',
      kind: 'delegated',
      keywords: ['delete'],
      available: mode !== 'chat',
      shortcutMac: '⌫',
      shortcutOther: 'Delete',
    }),
    command({
      id: 'edit.undo',
      label: 'Undo',
      group: 'Edit',
      kind: 'delegated',
      keywords: ['undo'],
      available: mode !== 'chat',
      shortcutMac: '⌘Z',
      shortcutOther: 'Ctrl+Z',
    }),
    command({
      id: 'edit.redo',
      label: 'Redo',
      group: 'Edit',
      kind: 'delegated',
      keywords: ['redo'],
      available: mode === 'flow',
      unavailableReason: mode === 'flow' ? undefined : 'Redo is on the flow canvas',
      shortcutMac: '⌘⇧Z',
      shortcutOther: 'Ctrl+Shift+Z',
    }),
    command({
      id: 'edit.duplicate',
      label: 'Duplicate',
      group: 'Edit',
      kind: 'delegated',
      keywords: ['duplicate'],
      available: mode === 'flow',
      shortcutMac: '⌘D',
      shortcutOther: 'Ctrl+D',
    }),
    command({
      id: 'edit.copy',
      label: 'Copy',
      group: 'Edit',
      kind: 'delegated',
      keywords: ['copy'],
      available: mode === 'flow',
      shortcutMac: '⌘C',
      shortcutOther: 'Ctrl+C',
    }),
    command({
      id: 'edit.paste',
      label: 'Paste',
      group: 'Edit',
      kind: 'delegated',
      keywords: ['paste'],
      available: mode === 'flow',
      shortcutMac: '⌘V',
      shortcutOther: 'Ctrl+V',
    }),
    command({
      id: 'edit.rename',
      label: 'Rename selection',
      group: 'Edit',
      kind: 'delegated',
      keywords: ['rename'],
      available: mode === 'flow',
      shortcutMac: 'F2',
      shortcutOther: 'F2',
    }),
    command({
      id: 'view.pan',
      label: 'Pan canvas',
      group: 'View',
      kind: 'delegated',
      keywords: ['pan', 'space'],
      available: mode !== 'chat',
      unavailableReason: mode === 'chat' ? 'Chat has no canvas' : undefined,
      shortcutMac: 'Space',
      shortcutOther: 'Space',
    }),
  ]
}

export function publishedMatrix(host: WorkspaceHost, mode: WorkspaceMode, selectionCount = 0, chromeHidden = false): WorkspaceCommand[] {
  return describe(host, mode, selectionCount, chromeHidden)
}

export function executableCommands(host: WorkspaceHost, mode: WorkspaceMode, selectionCount = 0, chromeHidden = false): WorkspaceCommand[] {
  return publishedMatrix(host, mode, selectionCount, chromeHidden).filter(item => item.available && item.kind !== 'delegated' && item.kind !== 'unavailable')
}

export interface ShortcutHelpGroup {
  title: string
  shortcuts: { id: string; keys: string; description: string; available: boolean; reason?: string }[]
}

export function shortcutHelp(host: WorkspaceHost, mode: WorkspaceMode, platform: WorkspacePlatform, selectionCount = 0, chromeHidden = false): ShortcutHelpGroup[] {
  const groups = new Map<string, ShortcutHelpGroup>()
  for (const item of publishedMatrix(host, mode, selectionCount, chromeHidden)) {
    const group = groups.get(item.group) ?? { title: item.group, shortcuts: [] }
    group.shortcuts.push({
      id: item.id,
      keys: platform === 'mac' ? item.shortcutMac : item.shortcutOther,
      description: item.label,
      available: item.available,
      reason: item.unavailableReason,
    })
    groups.set(item.group, group)
  }
  return [...groups.values()]
}

export function searchCommands(host: WorkspaceHost, mode: WorkspaceMode, query: string, selectionCount = 0): WorkspaceCommand[] {
  const needle = query.trim().toLowerCase()
  return executableCommands(host, mode, selectionCount, false).filter(item => {
    if (!needle) return true
    return item.label.toLowerCase().includes(needle) || item.keywords.some(word => word.includes(needle)) || item.id.includes(needle)
  })
}

function typingTarget(target: KeyTarget | null | undefined): boolean {
  if (!target) return false
  const tag = target.tagName?.toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return BLOCK_SELECTORS.some(selector => Boolean(target.closest?.(selector)))
}

function slashCommand(target: KeyTarget | null | undefined, event: WorkspaceKeyEvent): boolean {
  if (!target) return false
  const tag = target.tagName?.toUpperCase()
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !target.isContentEditable) return false
  if (event.key === '/' && !event.metaKey && !event.ctrlKey) return true
  return typeof target.value === 'string' && target.value.startsWith('/')
}

function chordMatches(event: WorkspaceKeyEvent, binding: Binding): boolean {
  const mod = Boolean(event.metaKey || event.ctrlKey)
  if (mod !== binding.mod || Boolean(event.shiftKey) !== binding.shift || Boolean(event.altKey) !== binding.alt) return false
  if (event.key.toLowerCase() === binding.key.toLowerCase()) return true
  return Boolean(binding.code && event.code === binding.code)
}

/**
 * Resolve a key to a workspace command, or null when the canvas, a field, or
 * the operating system should keep it. Returning null is the old behavior for
 * every mode and panel chord — callers must not invent a second map.
 */
export function resolveWorkspaceKey(event: WorkspaceKeyEvent, state: WorkspaceKeyState): WorkspaceCommand | null {
  if (event.repeat || event.isComposing || event.keyCode === 229) return null
  if (state.editorOpen || typingTarget(state.target) || slashCommand(state.target, event)) return null
  const binding = BINDINGS.find(item => chordMatches(event, item))
  if (!binding) return null
  const command = describe(state.host, state.mode, state.selectionCount, state.chromeHidden).find(item => item.id === binding.id)
  if (!command?.available) return null
  if (command.kind === 'delegated' || command.kind === 'unavailable') return null
  return command
}

/**
 * Apply a resolved command. Hide/show only flips `chromeHidden`. Panel flags,
 * dock layout, and selection stay the same objects so the open UI comes back
 * exactly, including dimensions stored beside those flags.
 */
export function applyWorkspaceCommand(state: ChromeState, command: WorkspaceCommand): { state: ChromeState; effect: WorkspaceEffect } {
  if (command.id === 'chrome.toggle-ui') {
    return { state: { ...state, chromeHidden: !state.chromeHidden }, effect: { type: 'chrome', hidden: !state.chromeHidden } }
  }
  if (command.kind === 'panel' && command.panel) {
    if (state.chromeHidden) return { state, effect: { type: 'none' } }
    const open = !state.visibility[command.panel]
    return {
      state: { ...state, visibility: { ...state.visibility, [command.panel]: open } },
      effect: { type: 'panel', panel: command.panel, open },
    }
  }
  if (command.kind === 'mode' && command.mode) {
    return { state: { ...state, mode: command.mode }, effect: { type: 'mode', mode: command.mode } }
  }
  if (command.id === 'help.shortcuts') return { state, effect: { type: 'help' } }
  if (command.id === 'search.actions') return { state, effect: { type: 'search', which: 'actions' } }
  if (command.id === 'search.tools') return { state, effect: { type: 'search', which: 'tools' } }
  if (command.id === 'view.zoom-in') return { state, effect: { type: 'zoom', action: 'in' } }
  if (command.id === 'view.zoom-out') return { state, effect: { type: 'zoom', action: 'out' } }
  if (command.id === 'view.fit-selection') return { state, effect: { type: 'zoom', action: 'selection' } }
  if (command.id === 'view.fit-canvas') return { state, effect: { type: 'zoom', action: 'fit' } }
  if (command.id === 'view.zoom-100') return { state, effect: { type: 'zoom', action: 'reset' } }
  if (command.id === 'edit.export') return { state, effect: { type: 'export' } }
  return { state, effect: { type: 'none' } }
}

export function workspaceModeFromDesktop(mode: 'linear' | 'frame' | 'graph'): WorkspaceMode {
  if (mode === 'linear') return 'chat'
  if (mode === 'frame') return 'board'
  return 'flow'
}

export function desktopModeFromWorkspace(mode: WorkspaceMode): 'linear' | 'frame' | 'graph' {
  if (mode === 'chat') return 'linear'
  if (mode === 'board') return 'frame'
  return 'graph'
}
